import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet } from "react-router-dom";
import type { RouteRecord } from "vite-react-ssg";

import Index from "./pages/Index";
import CookieConsent from "./components/habitus/CookieConsent";
import NotFound from "./pages/NotFound";
import { buscarDestaques, buscarPost, buscarPosts } from "./hooks/useBlogPosts";
import { prepararSanitizador } from "./lib/sanitizarHtml";
import { supabase } from "./integrations/supabase/client";

const queryClient = new QueryClient();

// Providers globais. Antes ficavam em volta do <BrowserRouter>; agora sao o
// elemento da rota raiz e todas as paginas entram pelo <Outlet />.
const Layout = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Outlet />
      {/* Banner de consentimento em todas as rotas (LGPD) */}
      <CookieConsent />
    </TooltipProvider>
  </QueryClientProvider>
);

// Code splitting por rota. Antes era React.lazy + <Suspense>; agora e o `lazy`
// do proprio react-router, que o vite-react-ssg sabe resolver em tempo de build
// para prerenderizar a pagina.
//
// Sem o campo opcional `entry` de proposito: com ele, o collectModules do
// vite-react-ssg caminha o grafo de modulos do manifest e estoura o stack
// ("Maximum call stack size exceeded") por causa dos imports circulares entre
// paginas e componentes. Ele so serve para "prehydration style loss", que aqui
// nao acontece -- o CSS das paginas publicas esta todo no bundle principal.
const page = (
  path: string,
  importer: () => Promise<{ default: React.ComponentType }>,
): RouteRecord => ({
  path,
  lazy: async () => ({ Component: (await importer()).default }),
});

// Um loader que falha derruba o build inteiro. Se o Supabase estiver fora do ar
// no momento do deploy, e melhor publicar a pagina sem os posts do que nao
// publicar -- o React Query busca de novo no navegador.
const semQuebrar = async <T,>(fn: () => Promise<T>, aoFalhar: T): Promise<T> => {
  try {
    return await fn();
  } catch (erro) {
    console.warn("[ssg] loader falhou, seguindo sem os dados:", erro);
    return aoFalhar;
  }
};

export const routes: RouteRecord[] = [
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        Component: Index,
        // Alimenta a secao "Do nosso blog" (BlogHighlights).
        loader: () => semQuebrar(buscarDestaques, []),
      },

      // Tratamentos
      page("medicina-integrativa", () => import("./pages/MedicinaIntegrativa")),
      page("soroterapia", () => import("./pages/Soroterapia")),
      page("ozonioterapia", () => import("./pages/Ozonioterapia")),
      // /hidrovitalis DESATIVADA em 22/09/2026 (auditoria CFM): detox iônico de
      // pés não é método reconhecido pelo CFM (Res. 2.336/2023, art. 11, VII).
      // Enquanto desativada, a URL responde 307 para / via vercel.json.
      // page("hidrovitalis", () => import("./pages/Hidrovitalis")),
      page("energia-e-foco", () => import("./pages/EnergiaEFoco")),
      page("fibromialgia-e-artrite", () => import("./pages/FibromialgiaEArtrite")),
      page("pos-bariatrica", () => import("./pages/PosBariatrica")),
      page("emagreca-mais", () => import("./pages/EmагrecaMais")),
      // Páginas de plano e o Habitus Wellness DESATIVADAS em 22/09/2026 por
      // decisão do cliente, após a auditoria CFM: os planos anunciavam pacote
      // de procedimentos com preço fechado (Manual da Codame, art. 9º, VI) e o
      // Wellness oferecia hormônio e PRP para performance (Res. CFM 2.333/2023
      // e 2.464/2026). Enquanto desativadas, as URLs respondem 307 para / via
      // vercel.json. Para reativar: descomentar aqui, devolver as entradas em
      // navigation.ts / QUICK_LINKS / tratamentosRelacionados.ts, public/llms.txt
      // e nas edge functions sitemap e llms-full (reimplantar), e remover os
      // redirects do vercel.json. Antes de reativar, revisar o conteúdo.
      // page("personalizado-para-voce", () => import("./pages/PersonalizadoParaVoce")),
      page("reposicao-hormonal", () => import("./pages/ReposicaoHormonal")),
      page("bioimpedancia", () => import("./pages/Bioimpedancia")),
      page("estetica-regenerativa", () => import("./pages/EsteticaRegenerativa")),
      // page("habitus-wellness", () => import("./pages/HabitusWellness")),
      // A /drclaudiomessias (cirurgia plástica) saiu em 27/10/2026: o Dr. Claudio
      // Messias deixou de atender na clínica. Página, dados e fotos foram
      // apagados do repositório; a URL responde 308 para /dradianasevilha via
      // vercel.json, porque é a página que ficou com flacidez e contorno.
      // Landing page da Dra. Diana Sevilha (estética regenerativa), URL final
      // das campanhas de flacidez, lipedema, contorno corporal, rejuvenescimento
      // facial e queda de cabelo. Usa o WhatsApp da clínica (passa pelo ConectAI).
      page("dradianasevilha", () => import("./pages/DraDianaSevilha")),
      page("nutricionista-larissa", () => import("./pages/NutriLarissa")),
      // URLs antigas do programa (que se chamava Performa+). O /performa-mais
      // redireciona com 301 no vercel.json; o /performa+ NAO pode virar
      // redirect la (o `source` e um padrao de rota, o `+` e lido como
      // quantificador de regex e a Vercel recusa o deploy inteiro com
      // "Invalid route source pattern") — entao vira uma pagina
      // prerenderizada de redirect (meta refresh + navigate).
      // Com o Habitus Wellness desativado, o /performa+ leva para a home.
      page("performa+", () => import("./pages/RedirecionarHabitusWellness")),

      // Landing page de ativacao do evento de 22/08/2026 (QR code do banner).
      // E `noindex` e NAO entra no sitemap nem no llms-full de proposito: ela
      // fala do mesmo programa que a /habitus-wellness, que e a versao que
      // disputa busca organica. Ver src/pages/Wellness.tsx.
      // DESATIVADA em 22/09/2026 junto com o Habitus Wellness; o QR impresso
      // continua funcionando porque a URL responde 307 para / via vercel.json.
      // page("wellness", () => import("./pages/Wellness")),

      // Institucionais e campanhas
      page("sobre-a-clinica", () => import("./pages/SobreAClinica")),
      page("links", () => import("./pages/Links")),
      page("trabalhe-na-habitus", () => import("./pages/TrabalheConosco")),
      page("faq", () => import("./pages/FAQ")),
      // Campanha sazonal de marco, DESATIVADA em 31/07/2026 (decisao do
      // cliente: volta no ano que vem). A pagina e os dados continuam no
      // repositorio (src/pages/DiaDasMulheres.tsx, src/data/diaMulheres.ts,
      // src/components/habitus/dia-mulheres/). Enquanto desativada, a URL
      // responde 307 para /plano-personalizado via vercel.json. Para reativar:
      // descomentar a rota abaixo, devolver a entrada em sitemapRoutes.ts,
      // public/sitemap.xml, public/llms.txt e na edge function sitemap
      // (reimplantar), e remover o redirect do vercel.json.
      // page("mes-das-mulheres", () => import("./pages/DiaDasMulheres")),
      // /plano-personalizado DESATIVADA em 22/09/2026 — ver a nota acima, junto
      // da /personalizado-para-voce.
      // page("plano-personalizado", () => import("./pages/PlanoPersonalizado")),

      // Legais
      page("politica-de-privacidade", () => import("./pages/PoliticaPrivacidade")),
      page("termo-de-uso", () => import("./pages/TermoDeUso")),
      page("politica-de-cookies", () => import("./pages/PoliticaDeCookies")),

      // Blog
      {
        ...page("blog", () => import("./pages/Blog")),
        loader: () => semQuebrar(() => buscarPosts(), []),
      },
      {
        ...page("blog/:slug", () => import("./pages/BlogPost")),
        loader: async ({ params }) => {
          // O sanitizador do servidor precisa de um DOM (jsdom) e a montagem e
          // assincrona, entao tem que acontecer aqui -- o render em si e
          // sincrono. Ver src/lib/sanitizarHtml.ts.
          await prepararSanitizador();
          return semQuebrar(() => buscarPost(params.slug ?? ""), null);
        },
        // Diz ao vite-react-ssg quais posts virar HTML. Sem isto a rota e
        // dinamica e nao seria prerenderizada -- e um post so existiria para o
        // crawler depois que o JavaScript rodasse.
        getStaticPaths: async () => {
          const { data, error } = await supabase
            .from("blog_posts")
            .select("slug")
            .eq("status", "published");

          if (error || !data) {
            console.warn("[ssg] nao consegui listar os slugs do blog:", error);
            return [];
          }
          return data.map((p) => `/blog/${p.slug}`);
        },
      },

      // Admin: fica FORA do prerender (ver includedRoutes no vite.config.ts).
      // Estas paginas importam react-quill-new, xlsx e recharts, que tocam
      // `document` no import e quebram o build em Node -- e prerenderizar so
      // produziria o spinner de "Verificando autenticacao".
      page("admin/login", () => import("./pages/admin/Login")),
      page("admin/dashboard", () => import("./pages/admin/Dashboard")),
      page("admin/cadastro-evento", () => import("./pages/admin/CadastroEvento")),
      page("admin/qr-code", () => import("./pages/admin/QrCode")),
      page("admin/analytics", () => import("./pages/admin/Analytics")),
      page("admin/users", () => import("./pages/admin/Users")),
      page("admin/blog", () => import("./pages/admin/BlogList")),
      page("admin/blog/novo", () => import("./pages/admin/BlogEdit")),
      page("admin/blog/editar/:id", () => import("./pages/admin/BlogEdit")),

      // Caminho concreto so para o prerender gerar um HTML de "nao encontrado"
      // que a Vercel possa servir com status 404 de verdade (o build copia
      // dist/404/index.html para dist/404.html -- ver onFinished no
      // vite.config.ts). Sem isso, qualquer URL invalida caia no catch-all e
      // respondia 200 com a home inteira: um soft 404.
      { path: "404", Component: NotFound },
      { path: "*", Component: NotFound },
    ],
  },
];
